import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ActionBar } from "../components/insurance/view/action-bar";
import { RecordBanners } from "../components/insurance/view/record-banners";
import { SectionAreas } from "../components/insurance/view/section-areas";
import { SectionCertificate } from "../components/insurance/view/section-certificate";
import { SectionCertificates } from "../components/insurance/view/section-certificates";
import { SectionDescription } from "../components/insurance/view/section-description";
import { SectionFacilities } from "../components/insurance/view/section-facilities";
import { SectionFinancial } from "../components/insurance/view/section-financial";
import { SectionImages } from "../components/insurance/view/section-images";
import { SectionInsured } from "../components/insurance/view/section-insured";
import { SectionNav } from "../components/insurance/view/section-nav";
import { SectionPhotos } from "../components/insurance/view/section-photos";
import {
  SectionAttachments,
  SectionFsl,
  SectionHistory,
  SectionHousekeeping,
  SectionNotes,
  SectionSignature,
} from "../components/insurance/view/section-remaining";
import { SectionSummary } from "../components/insurance/view/section-summary";
import { SectionUnits } from "../components/insurance/view/section-units";
import { useConfirm } from "../components/ui/confirm-dialog";
import { ErrorState, PageLoading } from "../components/ui/loading";
import { useInsuranceRecord } from "../hooks/use-api";
import { useScrollSpy } from "../hooks/use-scroll-spy";
import { api } from "../lib/api";
import { viewSections } from "./insurance-view-sections";

function num(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function str(v: unknown): string | null {
  if (v == null) return null;
  return String(v);
}

function dt(v: unknown): string | null {
  if (v == null) return null;
  return String(v);
}

export function InsuranceViewPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useInsuranceRecord(id);
  const { scrollRef, activeSection, scrollToSection } = useScrollSpy(viewSections, data);
  const { confirm, dialog } = useConfirm();

  const handleSupersede = useCallback(async () => {
    const ok = await confirm({
      title: "Supersede record",
      description:
        "A new editable copy will be created and this original will be archived as superseded. It stays linked and available for reference.",
    });
    if (!ok) return;
    try {
      const result = await api.post<{ record: { id: number } }>(`/api/insurance/${id}/supersede`);
      toast.success("Record superseded and archived. You are now on the new copy.");
      navigate(`/insurance/${result.record.id}/edit`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to supersede";
      toast.error(msg);
    }
  }, [id, navigate, confirm]);

  if (isLoading) return <PageLoading />;
  if (error || !data?.record) return <ErrorState message="Record not found or failed to load." />;

  const r = data.record as Record<string, unknown>;
  const valuer = r.valuer as Record<string, unknown> | null;
  const propertyType = r.propertyType as Record<string, unknown> | null;
  const subsoil = r.subsoil as Record<string, unknown> | null;
  const supersedes = r.supersedes as Record<string, unknown> | null;
  const supersededBy = r.supersededBy as Record<string, unknown> | null;
  const areas = (r.areas as Record<string, unknown>[]) ?? [];
  const units = (r.units as Record<string, unknown>[]) ?? [];
  const facilities = (r.facilities as Record<string, unknown>[]) ?? [];
  const photos = (r.photos as Record<string, unknown>[]) ?? [];
  const attachments = (r.attachments as Record<string, unknown>[]) ?? [];

  return (
    <div className="-my-6">
      {/* Fixed header: title + actions */}
      <div className="sticky top-0 z-30 border-b bg-background px-1 py-3">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-2xl font-bold tracking-tight truncate">{str(r.address) || `Record #${id}`}</h1>
          <ActionBar
            recordId={Number(id)}
            contactEmail={str(r.contactEmail)}
            address={str(r.address)}
            propertyCategory={str(r.propertyCategory)}
            onSupersede={handleSupersede}
          />
        </div>
        <RecordBanners
          cancelled={!!r.cancelled}
          cancelledDate={dt(r.cancelledDate)}
          supersededById={supersededBy ? Number(supersededBy.id) : null}
          supersededByAddress={supersededBy ? str(supersededBy.address) : null}
          supersedesId={supersedes ? Number(supersedes.id) : null}
          supersedesAddress={supersedes ? str(supersedes.address) : null}
        />
      </div>

      <div className="flex items-start gap-4 pt-4">
        {/* Left column: section nav — fixed, doesn't scroll */}
        <div className="hidden lg:block w-[180px] shrink-0 sticky top-[84px] self-start max-h-[calc(100vh-150px)] overflow-y-auto">
          <SectionNav sections={viewSections} activeSection={activeSection} onNavigate={scrollToSection} />
        </div>

        {/* Right column: scrollable content */}
        <div ref={scrollRef} className="flex-1 min-w-0 space-y-5 pr-1">
          <SectionInsured
            data={{
              insured: str(r.insured),
              contactFullName: str(r.contactFullName),
              contactFirstName: str(r.contactFirstName),
              contactAddress1: str(r.contactAddress1),
              contactAddress2: str(r.contactAddress2),
              contactPhone: str(r.contactPhone),
              contactEmail: str(r.contactEmail),
              instructingParty: str(r.instructingParty),
              instructingEmail: str(r.instructingEmail),
              invoiceTo: str(r.invoiceTo),
              address: str(r.address),
              buildingName: str(r.buildingName),
              numberOfUnits: num(r.numberOfUnits),
              lat: num(r.latitude),
              lng: num(r.longitude),
            }}
          />
          <SectionCertificate
            data={{
              propertyType: propertyType ? str(propertyType.name) : null,
              useOcc: str(r.useOcc),
              yearBuilt: num(r.yearBuilt),
              eyrBuilt: num(r.eyrBuilt),
              land: str(r.land),
              subsoil: subsoil ? str(subsoil.name) : str(r.subsoilLegacy),
              upgradeRequirements: str(r.upgradeRequirements),
              heritageListing: str(r.heritageListing),
              seismicGrade: str(r.seismicGrade),
              pocFrom: dt(r.pocFrom),
              pocTo: dt(r.pocTo),
              valuerName: valuer ? str(valuer.name) : null,
              qualifications: valuer ? str(valuer.qualifications) : null,
              feeAmountExGst: num(r.feeAmountExGst),
              accountNumberForXero: str(r.accountNumberForXero),
            }}
          />
          <SectionUnits
            units={units.map((u) => ({
              id: str(u.id) ?? "",
              label: str(u.label) ?? "",
              use: str(u.use) ?? "Residential",
              configuration: str(u.configuration),
              grossFloorArea: num(u.grossFloorArea),
              includedInEstimate: u.includedInEstimate !== false,
            }))}
          />
          <SectionPhotos />
          <SectionDescription
            data={{
              assetDescription: str(r.assetDescription),
              construction: str(r.construction),
              recreationalFeatures: str(r.recreationalFeatures),
              retainingWalls: str(r.retainingWalls),
              specialFeatures: str(r.specialFeatures),
              features: str(r.features),
              detailedDescription: str(r.detailedDescription),
            }}
          />
          <SectionImages
            data={{
              imagesIntro: str(r.imagesIntro),
              photos: photos.map((p) => ({
                id: str(p.id) ?? "",
                url: str(p.s3Key) ?? "",
                caption: str(p.caption) ?? "",
              })),
            }}
          />
          <SectionFinancial
            data={{
              sModal: num(r.sModal),
              eModal: num(r.eModal),
              percentChange: num(r.percentChange),
              age: num(r.age),
              feesPercentage: num(r.feesPercentage),
              demolitionPercentage: num(r.demolitionPercentage),
              gst: num(r.gst),
              inflationDemoPlanMonths: num(r.inflationDemoPlanMonths),
              inflationConstructionMonths: num(r.inflationConstructionMonths),
              inflationYear1: num(r.inflationYear1),
              inflationYear2: num(r.inflationYear2),
              inflationYear3: num(r.inflationYear3),
              inflationYear4: num(r.inflationYear4),
            }}
          />
          <SectionAreas
            areas={areas.map((a) => ({
              id: str(a.id) ?? "",
              description: str(a.description) ?? "",
              eyrBuilt: num(a.eyrBuilt),
              area: num(a.area),
              multiple: num(a.multiple),
              rate: num(a.rate),
              addGst: !!a.addGst,
              totalReinstatementExclFees: num(a.calcReinstatementExcl),
              feesPercentage: num(r.feesPercentage),
              totalReinstatementInclFees: num(a.calcReinstatementIncl),
              firstYearMonths: num(a.firstYearMonths),
              demoPlanMonths: num(a.demoPlanMonths),
              constructionMonths: num(a.constructionMonths),
              reinstatementMonths: num(a.calcReinstatementMonths),
              inflationPctYear1: null,
              inflationAmtYear1: null,
              inflationPctYear2: null,
              inflationAmtYear2: null,
              inflationPctYear3: null,
              inflationAmtYear3: null,
              inflationPctYear4: null,
              inflationAmtYear4: null,
              reinstatementInflationTotal: num(a.calcInflationTotal),
              demolitionPercentage: num(r.demolitionPercentage),
              demolitionRate: null,
              demolitionCost: num(a.calcDemolitionCost),
              percentagePa: num(a.percentagePa),
              depreciationTotalPercentage: null,
              depreciation: num(a.calcDepreciation),
              drcForFsl: num(a.calcDrcForFsl),
            }))}
          />
          <SectionFacilities
            facilities={facilities.map((f) => ({
              id: str(f.id) ?? "",
              category: (str(f.category) as "RECREATIONAL" | "SPECIAL_FEATURE") ?? "RECREATIONAL",
              description: str(f.description) ?? "",
              area: num(f.area),
              multiple: num(f.multiple),
              rate: num(f.rate),
              addGst: !!f.addGst,
              totalReinstatementInclFees: num(f.calcReinstatementIncl),
              reinstatementInflationTotal: num(f.calcInflationTotal),
              demolitionCost: num(f.calcDemolitionCost),
            }))}
          />
          <SectionSummary
            data={{
              reinstatementEstimate: num(r.reinstatementEstimate),
              reinstatementEstimateRounded: num(r.reinstatementEstimate),
              reinstatementInflation: num(r.reinstatementInflation),
              reinstatementInflationRounded: num(r.reinstatementInflation),
              totalDemolition: num(r.totalDemolition),
              totalDemolitionRounded: num(r.totalDemolition),
              drcForFsl: num(r.drcForFsl),
              drcForFslRounded: num(r.drcForFsl),
              reinstatementPerUnit: num(r.reinstatementCostEstimatePerUnit),
              numberOfUnits: num(r.numberOfUnits),
            }}
          />
          <SectionNotes note={str(r.note)} />
          <SectionCertificates recordId={id ?? ""} />
          <SectionAttachments
            attachments={attachments.map((a) => ({
              id: str(a.id) ?? "",
              filename: str(a.fileName) ?? "",
              url: str(a.s3Key) ?? "",
            }))}
          />
          <SectionSignature signatureUrl={valuer ? str(valuer.signatureUrl) : null} />
          <SectionHousekeeping
            data={{
              excludeFromMailout: !!r.excludeFromMailout,
              isActive: !!r.isActive,
              cancelled: !!r.cancelled,
              cancelledDate: dt(r.cancelledDate),
              finalSentConfirmed: !!r.finalSentConfirmed,
            }}
          />
          <SectionHistory data={{ createdAt: dt(r.createdAt), updatedAt: dt(r.updatedAt) }} />
          <SectionFsl fslText={str(r.fslText)} />
          {/* Spacer so bottom sections can scroll to top */}
          <div className="h-[60vh] shrink-0" />
        </div>
      </div>
      {dialog}
    </div>
  );
}
